import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";

export interface Data {
  file: File;
  prompt: string;
}

export interface FromDocumentFormResult {
  success: boolean;
  data?: Data;
  errors?: ValidationError[];
}

export interface ValidationError {
  field: string;
  message: string;
}

@Component({
  selector: "app-from-document-form",
  templateUrl: "./from-document-form.component.html",
  styleUrl: "./from-document-form.component.scss",
})
export class FromDocumentFormComponent implements OnInit {
  public form: FormGroup;

  @Input() disabled: boolean = false;

  @Output() formSubmit = new EventEmitter<FromDocumentFormResult>();
  @Output() formChange = new EventEmitter<boolean>();

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.form = this.fb.group({
      prompt: [""],
      file: [null, [Validators.required]],
    });

    this.form.valueChanges.subscribe(() => {
      this.formChange.emit(this.isFormValid);
    });
  }

  get fileControl() {
    return this.form.get("file");
  }

  get isFileInvalid() {
    return this.fileControl?.invalid && this.fileControl?.touched;
  }

  get isFormValid(): boolean {
    return this.form.valid;
  }

  public submitForm(): void {
    if (this.disabled) {
      return;
    }

    const validationErrors = this.validateBeforeSubmit();
    if (validationErrors.length > 0) {
      this.formSubmit.emit({
        success: false,
        errors: validationErrors,
      });
      return;
    }

    const formData = this.getFormData();
    if (formData) {
      this.formSubmit.emit({
        success: true,
        data: formData,
      });
    }
  }

  public resetForm(): void {
    this.form.reset();
  }

  public getFormData(): Data | null {
    if (!this.isFormValid) {
      return null;
    }
    return {
      file: this.form.get("file")?.value,
      prompt: this.form.get("prompt")?.value?.trim() || "",
    };
  }

  private validateBeforeSubmit(): ValidationError[] {
    const errors: ValidationError[] = [];

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (control?.invalid) {
          errors.push({
            field: key,
            message: `${key} is required`,
          });
        }
      });
    }

    return errors;
  }
}
